import React , { useRef, useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Stack,
    Checkbox,
    Popover,
    Button,
} from "@mui/material";

import { get, map, isEmpty } from "lodash";
import CircularProgress from "./CircularProgress";
import Padlock from "../../assets/icons/Padlock";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import ClearIcon from '@mui/icons-material/Clear';

const TableComponent = ({
    headTable,
    dataTable,
    __,
    loading,
    toolbar,
    propsTable = {},
    propsContainer = {},
    propsTableCell = {},
    action = <></>,
    empty = "",
    filters = {},
    onFilterChange = () => {},
}) => {
    const { showFilterRow = false } = propsTable;
    const tableRef = useRef(null);
    const topScrollRef = useRef(null);
    const bottomScrollRef = useRef(null);
    const [tableScrollWidth, setTableScrollWidth] = useState(0);
    // Estado local para los inputs — evita re-render del padre en cada tecla
    const [localFilters, setLocalFilters] = useState(filters);
    const debounceRef = useRef(null);
    const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
    const [dateRangePopover, setDateRangePopover] = useState({ anchorEl: null, key: null });
    const [localDateRanges, setLocalDateRanges] = useState({});
    const [pendingDateRange, setPendingDateRange] = useState({ from: null, to: null });

    // Medimos el ancho real de la tabla para aplicar al scroll de arriba
    useEffect(() => {
        if (tableRef.current) {
            setTableScrollWidth(tableRef.current.scrollWidth);
        }
    }, [dataTable, headTable]);

    // Sincroniza scroll arriba <-> abajo
    const syncScroll = (source, target) => {
         if (source?.current && target?.current) {
            target.current.scrollLeft = source.current.scrollLeft;
        }
    };

    return (
        <Paper>
            {toolbar}
            <Box
                ref={topScrollRef}
                onScroll={() => syncScroll(topScrollRef, bottomScrollRef)}
                sx={{
                    overflowX: 'scroll',
                    overflowY: 'hidden',
                    height: 20,
                    mb: '2px',
                    width: '100%',
                }}
            >
                <Box sx={{ width: tableScrollWidth, height: 20 }} />
            </Box>

            <TableContainer {...propsContainer} ref={bottomScrollRef}
                onScroll={() => syncScroll(bottomScrollRef, topScrollRef)}
                sx={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto', ...propsContainer?.sx }}>
                <Table ref={tableRef} aria-label="table" stickyHeader {...propsTable}>
                    <TableHead>
                        <TableRow>
                            {map(headTable, ({ key, label, align, sx = {} }, i) => (
                                <TableCell key={i} align={align} {...propsTableCell} sx={{ ...sx, top: 0, backgroundColor: 'background.paper' }}>
                                    <Typography variant="buttonSmall" component="div">
                                        {label}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>

                        {showFilterRow && (
                            <TableRow>
                                {map(headTable, ({ key, align, sx = {}, filterType }, i) => {
                                const isReadOnly = key === "options" || key === "trackingUrl";
                                const isDateRangeField = filterType === "dateRange";
                                const isDateField = !isDateRangeField && key.toLowerCase().includes("date");
                                const isBoolField = filterType === "bool";

                                return (
                                    <TableCell key={i} align={align} {...propsTableCell} sx={{ ...sx, top: 57, backgroundColor: 'background.paper' }}>
                                    {isDateRangeField ? (
                                        <Box sx={{ position: 'relative', width: '100%' }}>
                                            <input
                                                type="text"
                                                value={(() => {
                                                    const r = localDateRanges[key] || {};
                                                    if (r.from && r.to) return `${r.from} ~ ${r.to}`;
                                                    if (r.from) return `${r.from} ~`;
                                                    if (r.to) return `~ ${r.to}`;
                                                    return '';
                                                })()}
                                                readOnly
                                                onClick={(e) => {
                                                    const r = localDateRanges[key] || {};
                                                    setPendingDateRange({ from: r.from || null, to: r.to || null });
                                                    setDateRangePopover({ anchorEl: e.currentTarget, key });
                                                }}
                                                placeholder="Filtrar"
                                                style={{
                                                    width: '100%',
                                                    padding: '4px 24px 4px 4px',
                                                    boxSizing: 'border-box',
                                                    fontSize: '0.8rem',
                                                    textAlign: 'center',
                                                    backgroundColor: 'white',
                                                    color: '#000',
                                                    cursor: 'pointer',
                                                }}
                                            />
                                            {(localDateRanges[key]?.from || localDateRanges[key]?.to) && (
                                                <ClearIcon
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setLocalDateRanges(prev => ({ ...prev, [key]: { from: null, to: null } }));
                                                        onFilterChange(key, { from: null, to: null });
                                                    }}
                                                    sx={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', fontSize: '16px', color: 'grey.500', '&:hover': { color: 'grey.700' } }}
                                                />
                                            )}
                                        </Box>
                                    ) : isBoolField ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                            <Checkbox
                                                size="small"
                                                indeterminate={localFilters[key] === "" || localFilters[key] === undefined}
                                                checked={localFilters[key] === true}
                                                onChange={() => {
                                                    const cur = localFilters[key];
                                                    const next = (cur === "" || cur === undefined) ? true : cur === true ? false : "";
                                                    setLocalFilters(prev => ({ ...prev, [key]: next }));
                                                    onFilterChange(key, next);
                                                }}
                                                sx={{ padding: '2px' }}
                                            />
                                        </Box>
                                    ) : isDateField ? (
                                        <DatePicker
                                        value={localFilters[key] ? dayjs(localFilters[key]) : null}
                                        onChange={(newValue) => {
                                            const formatted = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
                                            setLocalFilters(prev => ({ ...prev, [key]: formatted }));
                                            onFilterChange(key, formatted);
                                        }}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                            variant: 'standard',
                                            fullWidth: true,
                                            placeholder: 'Filtrar',
                                            InputProps: {
                                                disableUnderline: true,
                                                sx: {
                                                backgroundColor: 'white',
                                                color: '#000',
                                                textAlign: 'center',
                                                fontSize: '0.8rem',
                                                padding: '4px',
                                                }
                                            },
                                            inputProps: {
                                                style: {
                                                textAlign: 'center',
                                                fontSize: '0.8rem',
                                                padding: '4px',
                                                }
                                            }
                                            },
                                            field: {
                                                clearable: true
                                            }
                                        }}
                                        />
                                    ) : (
                                        <Box sx={{ position: 'relative', width: '100%' }}>
                                            <input
                                                type="text"
                                                value={localFilters[key] || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFilters(prev => ({ ...prev, [key]: value }));
                                                    if (debounceRef.current) clearTimeout(debounceRef.current);
                                                    if (value.length === 0) {
                                                        onFilterChange(key, value);
                                                    } else if (value.length >= 3) {
                                                        debounceRef.current = setTimeout(() => {
                                                            onFilterChange(key, value);
                                                        }, 300);
                                                    }
                                                }}
                                                readOnly={isReadOnly}
                                                placeholder={isReadOnly ? '' : 'Filtrar'}
                                                style={{
                                                width: '100%',
                                                padding: '4px 24px 4px 4px',
                                                boxSizing: 'border-box',
                                                fontSize: '0.8rem',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                color: '#000',
                                                cursor: isReadOnly ? 'default' : 'text',
                                                }}
                                            />
                                            {!isReadOnly && localFilters[key] && (
                                                <ClearIcon
                                                    onClick={() => {
                                                        setLocalFilters(prev => ({ ...prev, [key]: '' }));
                                                        onFilterChange(key, '');
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        right: 6,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        cursor: 'pointer',
                                                        fontSize: '16px',
                                                        color: 'grey.500',
                                                        '&:hover': {
                                                        color: 'grey.700',
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    )}
                                    </TableCell>
                                );
                                })}
                            </TableRow>
                        )}


                    </TableHead>

                    {!loading && (
                        <TableBody>
                            {dataTable.map((row, i) => (
                                <TableRow key={i}>
                                    {map(headTable, ({ key, align, sx = {} }, j) => (
                                        <TableCell key={j} align={align} {...propsTableCell} sx={sx}>
                                            <Typography variant="bodySmall" component="div">
                                                {get(row, key)}
                                            </Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    )}
                </Table>

                {loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            height: 300,
                            width: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {isEmpty(dataTable) && !loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: 500,
                            width: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Padlock width={100} height={100} />
                        <Stack justifyContent="center" alignItems="center" spacing={1} pt={2}>
                            <Typography variant="bml">
                                {empty ? __(`table.empty.${empty}.name`) : __(`table.empty.name`)}
                            </Typography>
                            <Typography variant="caption">
                                {empty ? __(`table.empty.${empty}.info`) : __(`table.empty.info`)}
                            </Typography>
                            <Box pt={2}>{action}</Box>
                        </Stack>
                    </Box>
                )}
            </TableContainer>

            <Popover
                open={Boolean(dateRangePopover.anchorEl)}
                anchorEl={dateRangePopover.anchorEl}
                onClose={() => setDateRangePopover({ anchorEl: null, key: null })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ mt: 1 }}
            >
                <Box sx={{ p: 2, width: 280 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.primary' }}>Rango de Fechas</Typography>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>Desde</Typography>
                            <DatePicker
                                value={pendingDateRange.from ? dayjs(pendingDateRange.from) : null}
                                onChange={(v) => setPendingDateRange(prev => ({ ...prev, from: v ? dayjs(v).format("YYYY-MM-DD") : null }))}
                                format="DD/MM/YYYY"
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>Hasta</Typography>
                            <DatePicker
                                value={pendingDateRange.to ? dayjs(pendingDateRange.to) : null}
                                onChange={(v) => setPendingDateRange(prev => ({ ...prev, to: v ? dayjs(v).format("YYYY-MM-DD") : null }))}
                                format="DD/MM/YYYY"
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </Box>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={() => {
                                    const k = dateRangePopover.key;
                                    setLocalDateRanges(prev => ({ ...prev, [k]: { from: null, to: null } }));
                                    onFilterChange(k, { from: null, to: null });
                                    setPendingDateRange({ from: null, to: null });
                                    setDateRangePopover({ anchorEl: null, key: null });
                                }}
                            >
                                Limpiar
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                    const k = dateRangePopover.key;
                                    setLocalDateRanges(prev => ({ ...prev, [k]: pendingDateRange }));
                                    onFilterChange(k, pendingDateRange);
                                    setDateRangePopover({ anchorEl: null, key: null });
                                }}
                            >
                                Aplicar
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Popover>
        </Paper>
    );
};

export default TableComponent;
